import { useState, useEffect } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DialogModal, ConfirmPopupModal } from "../../components/ui/dialogModal";
import { cn } from "../../utils/tailwindUtils";
import { RefreshCw, Trash2, Search, Loader2, ExternalLink, Info, Edit3, Eye } from "lucide-react";
import { Select, Switch } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import { WA_TEMPLATES_URL, WA_TEMPLATE_SYNC_URL, WA_ACCOUNTS_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";

const STATUS_COLORS = {
    approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    pending:  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    disabled: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};

const CATEGORY_OPTIONS = [
    { value: 'utility',        label: 'Utility' },
    { value: 'marketing',      label: 'Marketing' },
    { value: 'authentication', label: 'Authentication' },
];

const EMPTY_FORM = {
    template_name: '', category: 'utility', language: 'en',
    body_text: '', footer_text: '', header_type: 'none', header_content: '',
    buttons: '[]', variable_names: '[]', account_id: null,
};

export default function WhatsAppTemplates() {
    const { fetch, create, update, remove } = useApiMethods();
    const [templates, setTemplates] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [previewId, setPreviewId] = useState(null);

    const loadData = async () => {
        setLoading(true);
        const [tRes, aRes] = await Promise.all([
            fetch(WA_TEMPLATES_URL),
            fetch(WA_ACCOUNTS_URL),
        ]);
        if (tRes?.status === 'success') setTemplates(tRes.data || []);
        if (aRes?.status === 'success') setAccounts(aRes.data || []);
        setLoading(false);
    };

    useEffect(() => { loadData(); }, []);

    const handleSync = async () => {
        if (accounts.length === 0) {
            ToastMsg('error', 'No WhatsApp accounts connected');
            return;
        }
        setSyncing(true);
        const res = await create(WA_TEMPLATE_SYNC_URL, { account_id: accounts[0].id });
        setSyncing(false);
        if (res?.status === 'success') {
            ToastMsg('success', res.message || 'Templates synced');
            loadData();
        } else {
            ToastMsg('error', res?.message || 'Sync failed');
        }
    };

    const openCreate = () => {
        setEditingTemplate(null);
        setForm({ ...EMPTY_FORM, account_id: accounts[0]?.id || null });
        setDialogOpen(true);
    };

    const openEdit = (t) => {
        setEditingTemplate(t);
        setForm({
            template_name: t.template_name,
            category: t.category,
            language: t.language,
            body_text: t.body_text,
            footer_text: t.footer_text,
            header_type: t.header_type,
            header_content: t.header_content,
            buttons: t.buttons,
            variable_names: t.variable_names,
            account_id: t.account_id,
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.template_name || !form.body_text) {
            ToastMsg('error', 'Template name and body text are required');
            return;
        }
        setSaving(true);
        let res;
        if (editingTemplate) {
            res = await update(WA_TEMPLATES_URL, { id: editingTemplate.id, ...form });
        } else {
            res = await create(WA_TEMPLATES_URL, form);
        }
        setSaving(false);
        if (res?.status === 'success') {
            ToastMsg('success', editingTemplate ? 'Template updated' : 'Template created');
            setDialogOpen(false);
            loadData();
        } else {
            ToastMsg('error', res?.message || 'Save failed');
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        const res = await remove(`${WA_TEMPLATES_URL}?id=${deleteTarget.id}`);
        setDeleting(false);
        if (res?.status === 'success') {
            ToastMsg('success', 'Template deleted');
            setDeleteTarget(null);
            loadData();
        } else {
            ToastMsg('error', res?.message || 'Delete failed');
        }
    };

    const handleToggleDisable = async (t) => {
        const res = await update(WA_TEMPLATES_URL, { id: t.id, is_disabled_in_crm: !t.is_disabled_in_crm });
        if (res?.status === 'success') {
            setTemplates(prev => prev.map(x => x.id === t.id ? { ...x, is_disabled_in_crm: !x.is_disabled_in_crm } : x));
        } else {
            ToastMsg('error', 'Failed to update');
        }
    };

    const fc = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const filtered = templates.filter(t =>
        !search ||
        t.template_name.toLowerCase().includes(search.toLowerCase()) ||
        t.category.toLowerCase().includes(search.toLowerCase())
    );

    const previewTemplate = templates.find(t => t.id === previewId);

    // Extract variable positions from body text
    const extractVars = (text) => {
        const matches = [...(text || '').matchAll(/\{\{(\d+)\}\}/g)];
        return [...new Set(matches.map(m => m[1]))];
    };

    return (
        <div className="p-6 space-y-6">
            <ContainerCard>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">WhatsApp Templates</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            Meta-approved templates synced from WATI
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            onClick={handleSync}
                            disabled={syncing}
                            className="flex items-center gap-2 text-sm"
                        >
                            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            Sync from WATI
                        </Button>
                        <a
                            href="https://app.wati.io/message-templates"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Button variant="outline" className="flex items-center gap-2 text-sm">
                                <ExternalLink className="h-4 w-4" /> Create in WATI
                            </Button>
                        </a>
                    </div>
                </div>

                {/* Info banner */}
                <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
                    <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                        WhatsApp templates must be created and approved in your <strong>WATI dashboard</strong> first.
                        Once approved, click <strong>Sync from WATI</strong> to import them here.
                    </span>
                </div>

                <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        className="pl-9"
                        placeholder="Search templates..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                    <th className="pb-2 font-medium">Name</th>
                                    <th className="pb-2 font-medium">Category</th>
                                    <th className="pb-2 font-medium">Language</th>
                                    <th className="pb-2 font-medium">Status</th>
                                    <th className="pb-2 font-medium">Variables</th>
                                    <th className="pb-2 font-medium">Use in CRM</th>
                                    <th className="pb-2 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(t => {
                                    const vars = extractVars(t.body_text);
                                    return (
                                        <tr key={t.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                            <td className="py-3 font-medium text-gray-800 dark:text-gray-100">{t.template_name}</td>
                                            <td className="py-3 text-gray-600 dark:text-gray-400 capitalize">{t.category}</td>
                                            <td className="py-3 text-gray-600 dark:text-gray-400">{t.language}</td>
                                            <td className="py-3">
                                                <span className={cn('px-2 py-0.5 text-[11px] font-medium rounded-full', STATUS_COLORS[t.status] || STATUS_COLORS.pending)}>
                                                    {t.status}
                                                </span>
                                            </td>
                                            <td className="py-3">
                                                <div className="flex flex-wrap gap-1">
                                                    {vars.map(v => (
                                                        <span key={v} className="px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded font-mono">
                                                            {`{{${v}}}`}
                                                        </span>
                                                    ))}
                                                    {vars.length === 0 && <span className="text-xs text-gray-400">—</span>}
                                                </div>
                                            </td>
                                            <td className="py-3">
                                                <Switch
                                                    size="small"
                                                    checked={!t.is_disabled_in_crm}
                                                    onChange={() => handleToggleDisable(t)}
                                                    title={t.is_disabled_in_crm ? 'Enable for CRM use' : 'Disable for CRM use'}
                                                />
                                            </td>
                                            <td className="py-3">
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => setPreviewId(t.id)} className="text-gray-400 hover:text-blue-500" title="Preview">
                                                        <Eye className="h-4 w-4" />
                                                    </button>
                                                    <button onClick={() => openEdit(t)} className="text-gray-400 hover:text-blue-500" title="Edit">
                                                        <Edit3 className="h-4 w-4" />
                                                    </button>
                                                    <button onClick={() => setDeleteTarget(t)} className="text-gray-400 hover:text-red-500" title="Delete">
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="py-8 text-center text-sm text-gray-400">
                                            No templates found. Sync from WATI or create one manually.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </ContainerCard>

            {/* Create / Edit Dialog */}
            <DialogModal
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                title={editingTemplate ? 'Edit Template' : 'New Template'}
                width="560px"
                showActions={false}
            >
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Template Name *</label>
                            <Input value={form.template_name} onChange={e => fc('template_name', e.target.value)} placeholder="hello_world" />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Category</label>
                            <Select
                                className="w-full"
                                value={form.category}
                                options={CATEGORY_OPTIONS}
                                onChange={v => fc('category', v)}
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Language</label>
                            <Input value={form.language} onChange={e => fc('language', e.target.value)} placeholder="en" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Body Text *</label>
                        <textarea
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg p-2 text-sm bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={4}
                            value={form.body_text}
                            onChange={e => fc('body_text', e.target.value)}
                            placeholder="Hello {{1}}, your appointment is on {{2}}."
                        />
                        <p className="text-[10px] text-gray-400 mt-0.5">Use {'{{1}}'}, {'{{2}}'} for positional variables</p>
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Footer Text</label>
                        <Input value={form.footer_text} onChange={e => fc('footer_text', e.target.value)} placeholder="Reply STOP to unsubscribe" />
                    </div>
                    {/* Body preview */}
                    {form.body_text && (
                        <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-3 border border-green-200 dark:border-green-800">
                            <p className="text-[10px] font-medium text-green-600 dark:text-green-400 mb-1">Preview</p>
                            <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{form.body_text}</p>
                            {form.footer_text && <p className="text-xs text-gray-400 mt-1">{form.footer_text}</p>}
                        </div>
                    )}
                    <div className="flex gap-2 pt-2">
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 text-sm">Cancel</Button>
                        <Button onClick={handleSave} disabled={saving} className="flex-1 text-sm flex items-center gap-2 justify-center">
                            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                            {editingTemplate ? 'Update' : 'Create'}
                        </Button>
                    </div>
                </div>
            </DialogModal>

            {/* Preview Dialog */}
            <DialogModal
                open={!!previewId}
                onClose={() => setPreviewId(null)}
                title="Template Preview"
                width="420px"
                showActions={false}
            >
                {previewTemplate && (
                    <div className="space-y-3">
                        <div className="bg-[#dcf8c6] dark:bg-green-900/20 rounded-2xl rounded-tr-none p-4 max-w-xs ml-auto shadow-sm">
                            <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{previewTemplate.body_text}</p>
                            {previewTemplate.footer_text && (
                                <p className="text-xs text-gray-500 mt-2 border-t border-green-300/50 dark:border-green-700/50 pt-1">{previewTemplate.footer_text}</p>
                            )}
                        </div>
                        <div className="text-xs text-gray-400 text-center">
                            {previewTemplate.template_name} · {previewTemplate.language} · {previewTemplate.category}
                        </div>
                    </div>
                )}
            </DialogModal>

            {/* Delete Confirm */}
            <ConfirmPopupModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                loading={deleting}
                title="Delete Template"
                description={`Are you sure you want to delete "${deleteTarget?.template_name}"?`}
            />
        </div>
    );
}
